import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'

import { useClickOutside } from '../lib/useClickOutside'
import { useFocusTrap } from '../lib/useFocusTrap'

type ConfirmTone = 'default' | 'danger'

type ConfirmOptions = {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmTone
}

type ConfirmState = ConfirmOptions & {
  resolve: (value: boolean) => void
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

function ToneIcon({ tone }: { tone: ConfirmTone }) {
  if (tone === 'danger') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a1.5 1.5 0 0 0 1.29 2.25h17.78a1.5 1.5 0 0 0 1.3-2.25L13.7 3.86a1.5 1.5 0 0 0-2.6 0Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 8h.01M11 12h1v5h1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    </svg>
  )
}

/** Confirmación estilo SWAL: reemplaza `window.confirm` con un diálogo propio
 *  que sí respeta el tema y la tipografía de la app. Es imperativa a
 *  propósito —`await confirm({ ... })` en vez de un componente controlado por
 *  cada página— porque es exactamente así como se usa `window.confirm`, y el
 *  reemplazo no debería pedir manejar estado propio en cada lugar que lo usa. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmState | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  const confirm = useCallback<ConfirmFn>(
    (options) => new Promise((resolve) => setState({ ...options, resolve })),
    [],
  )

  const settle = (value: boolean) => {
    setState((current) => {
      current?.resolve(value)
      return null
    })
  }

  useClickOutside(ref, () => settle(false), state !== null)
  useFocusTrap(ref, state !== null)

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div className="confirm-backdrop">
          <div className="confirm-dialog" ref={ref} role="alertdialog" aria-modal="true" tabIndex={-1}>
            <div className={`confirm-dialog__icon confirm-dialog__icon--${state.tone ?? 'default'}`}>
              <ToneIcon tone={state.tone ?? 'default'} />
            </div>
            <h3>{state.title}</h3>
            {state.description && <p className="muted">{state.description}</p>}
            <div className="confirm-dialog__actions">
              {/* En `danger`, el foco inicial es Cancelar, no Confirmar —
                  antes un Enter reflejo (el gesto más común para cerrar un
                  diálogo recién abierto) borraba/destruía de una, sin
                  ningún paso intermedio. */}
              <button
                className="button button--ghost"
                onClick={() => settle(false)}
                autoFocus={state.tone === 'danger'}
              >
                {state.cancelLabel ?? 'Cancelar'}
              </button>
              <button
                className={state.tone === 'danger' ? 'button button--danger' : 'button'}
                onClick={() => settle(true)}
                autoFocus={state.tone !== 'danger'}
              >
                {state.confirmLabel ?? 'Aceptar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm se usa dentro de <ConfirmProvider>')
  return ctx
}
